import React, { useEffect, useMemo, useState } from 'react';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useAppDispatch, useAppSelector } from '../../../store';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';

import BuildNumberColumn from './BuildNumberColumn';
import { selectServiceMissionsData } from '../../../store/service/service-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { useTranslation } from 'react-i18next';
import BuildNumberFilters from './BuildNumberFilters';
import { getStatsMissionEntries } from '../../../store/service/service-actions';

const BuildOverviewPage: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const missionsData = useAppSelector((state) => selectServiceMissionsData(state));

  const [showOnlyRepeatedDeployments, setShowOnlyRepeatedDeployments] = useState(false);

  useEffect(() => {
    missionsData.entries.forEach((buildSet) => {
      if (buildSet.rest.getStatus === HttpFetchStatus.Idle) {
        dispatch(
          getStatsMissionEntries({
            buildNumber: buildSet.buildNumber,
            softwareVersion: buildSet.softwareVersion,
          }),
        );
      }
    });
  }, [dispatch, missionsData]);

  const {
    value: searchBuildNumber,
    validationResult: searchBuildNumberValidationResult,
    valueChangeHandler: searchBuildNumberChangeHandler,
  } = useValidatedInputEffect(
    '',
    (buildNumber) => ({ isValid: true, error: t('form-help-text-search-build-number') }),
    (_) => () => {},
    { isValid: true, error: null },
  );

  const repeatedDeploymentsToggleHandler = () => {
    setShowOnlyRepeatedDeployments((prevState) => !prevState);
  };

  const repeatedDeploymentIds: string[] = useMemo(() => {
    if (missionsData.entries.length === 0) return [];

    let commonDeploymentIds = new Set(
      missionsData.entries[0].data.map((mission) => mission.deploymentid),
    );

    for (let i = 1; i < missionsData.entries.length; i++) {
      const currentItemIds = new Set(
        missionsData.entries[i].data.map((mission) => mission.deploymentid),
      );
      commonDeploymentIds = new Set(
        [...commonDeploymentIds].filter((id) => currentItemIds.has(id)),
      );

      // Early exit if nothing in common
      if (commonDeploymentIds.size === 0) return [];
    }

    return [...commonDeploymentIds];
  }, [missionsData.entries]);

  return (
    <Grid container spacing={2} pt={2}>
      <Grid item xs={12}>
        <BuildNumberFilters
          searchBuildNumber={searchBuildNumber}
          onSearchBuildNumberChange={searchBuildNumberChangeHandler}
          searchBuildNumberValidationResult={searchBuildNumberValidationResult}
          repeatedDeployments={{
            value: showOnlyRepeatedDeployments,
            onToggle: repeatedDeploymentsToggleHandler,
          }}
        />
      </Grid>

      {missionsData.entries.length > 0 && (
        <Grid item container spacing={2} xs={12}>
          {[...missionsData.entries]
            .sort((a, b) => 0 - (a.buildNumber > b.buildNumber ? 1 : -1))
            .map((buildSet, index) => (
              <React.Fragment key={buildSet.buildNumber + buildSet.softwareVersion}>
                {index > 0 && (
                  <Divider sx={{ mr: '-1px', mt: 2 }} orientation="vertical" flexItem />
                )}
                <Grid item xs={12 / missionsData.entries.length}>
                  <BuildNumberColumn
                    repeatedDeploymentIds={
                      showOnlyRepeatedDeployments ? repeatedDeploymentIds : null
                    }
                    buildSet={{
                      ...buildSet,
                      data: buildSet.data.filter(
                        (mission) =>
                          !showOnlyRepeatedDeployments ||
                          repeatedDeploymentIds.includes(mission.deploymentid),
                      ),
                    }}
                    multiple={missionsData.entries.length > 1}
                  />
                </Grid>
              </React.Fragment>
            ))}
        </Grid>
      )}
    </Grid>
  );
};

export default BuildOverviewPage;
